/**
 * Modération chat étudiants ↔ étudiants (étape 1 : lecture seule).
 */
import React, { useEffect, useState } from 'react';
import { MessageSquare, Users, Search, X, Eye, Flag, Loader2, Trash2, AlertTriangle, Check, Archive } from 'lucide-react';
import { getAnneeAcademique } from '../../services/baseApi';

const API = (process.env.REACT_APP_API_URL as string) || '/api';

interface EtuLite { matricule: string; nom: string; prenoms: string; photo_url: string | null; classe: string; }
interface ConvAdmin {
  id: number;
  type: 'direct' | 'groupe';
  nom: string;
  date_creation: string;
  derniere_activite: string;
  nb_participants: number;
  nb_messages: number;
  nb_signales: number;
  participants: EtuLite[];
  dernier_message_extrait: string;
  archivee: boolean;
}
interface MsgAdmin {
  id: number;
  expediteur: EtuLite;
  type: string;
  contenu: string;
  fichier_url: string | null;
  date_envoi: string;
  supprime: boolean;
  supprime_par: string;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token') || '';
  const annee = getAnneeAcademique();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(annee ? { 'X-Annee-Academique': annee } : {}),
  };
}

interface SignalementAdmin {
  id: number;
  statut: 'en_attente' | 'traite' | 'rejete';
  motif: string;
  date_signalement: string;
  signaleur: EtuLite;
  message: { id: number; contenu: string; type: string; fichier_url: string | null; date_envoi: string; supprime: boolean; expediteur: EtuLite };
  conversation_id: number;
  note_admin: string;
  traite_par_nom: string;
  date_traitement: string | null;
}

export const ChatModeration: React.FC = () => {
  const [tab, setTab] = useState<'conversations' | 'signalements'>('conversations');
  const [convs, setConvs] = useState<ConvAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [type, setType] = useState<'' | 'direct' | 'groupe'>('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<{ conversation: any; participants: EtuLite[]; messages: MsgAdmin[] } | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Signalements
  const [signalements, setSignalements] = useState<SignalementAdmin[]>([]);
  const [statutFilter, setStatutFilter] = useState<'en_attente' | 'traite' | 'rejete' | 'tous'>('en_attente');
  const [nbEnAttente, setNbEnAttente] = useState(0);

  const loadSignalements = async () => {
    try {
      const res = await fetch(`${API}/portail/admin/chat/signalements/?statut=${statutFilter}&limit=200`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSignalements(data.results || []);
        setNbEnAttente(data.nb_en_attente || 0);
      }
    } catch { /* ignore */ }
  };
  useEffect(() => { loadSignalements(); /* eslint-disable-next-line */ }, [statutFilter]);
  useEffect(() => {
    const i = setInterval(loadSignalements, 30000);
    return () => clearInterval(i);
    /* eslint-disable-next-line */
  }, [statutFilter]);

  const traiterSignalement = async (sigId: number, action: 'supprimer_message' | 'rejeter') => {
    const note = window.prompt(`Note (facultatif) :`, '');
    if (note === null) return;
    try {
      const res = await fetch(`${API}/portail/admin/chat/signalements/${sigId}/traiter/`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, note }),
      });
      if (res.ok) loadSignalements();
      else alert((await res.json())?.detail || 'Erreur');
    } catch (e: any) { alert(e?.message || 'Erreur'); }
  };

  const supprimerMessageDetail = async (msgId: number) => {
    const raison = window.prompt('Raison de la suppression (5 caractères min) :');
    if (!raison || raison.trim().length < 5) return;
    try {
      const res = await fetch(`${API}/portail/admin/chat/messages/${msgId}/supprimer/`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ raison: raison.trim() }),
      });
      if (res.ok) {
        if (selectedId) loadDetail(selectedId);
      } else {
        alert((await res.json())?.detail || 'Erreur');
      }
    } catch (e: any) { alert(e?.message || 'Erreur'); }
  };

  const archiverConv = async (convId: number) => {
    if (!window.confirm('Archiver / désarchiver cette conversation ? (Cachée des étudiants si archivée)')) return;
    try {
      const res = await fetch(`${API}/portail/admin/chat/conversations/${convId}/archiver/`, {
        method: 'POST', headers: authHeaders(),
      });
      if (res.ok) { load(); if (selectedId) loadDetail(selectedId); }
    } catch (e: any) { alert(e?.message || 'Erreur'); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (type) params.set('type', type);
      if (q) params.set('q', q);
      const res = await fetch(`${API}/portail/admin/chat/conversations/?${params}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setConvs(data.results || []);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const loadDetail = async (id: number) => {
    setLoadingDetail(true);
    setSelectedId(id);
    try {
      const res = await fetch(`${API}/portail/admin/chat/conversations/${id}/`, { headers: authHeaders() });
      if (res.ok) setDetail(await res.json());
    } finally { setLoadingDetail(false); }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
      {/* Onglets */}
      <div className="border-b border-gray-200 dark:border-gray-700 px-4 pt-3 flex gap-2">
        <button
          onClick={() => setTab('conversations')}
          className={`px-3 py-2 text-sm font-medium border-b-2 ${
            tab === 'conversations' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500'
          }`}
        >
          <MessageSquare size={14} className="inline mr-1" /> Conversations
        </button>
        <button
          onClick={() => setTab('signalements')}
          className={`px-3 py-2 text-sm font-medium border-b-2 inline-flex items-center gap-1.5 ${
            tab === 'signalements' ? 'border-orange-500 text-orange-600 dark:text-orange-400' : 'border-transparent text-gray-500'
          }`}
        >
          <Flag size={14} /> Signalements
          {nbEnAttente > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-red-500 text-white rounded-full px-1">
              {nbEnAttente}
            </span>
          )}
        </button>
      </div>

      {tab === 'signalements' ? (
        <div>
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
            <select
              value={statutFilter}
              onChange={(e) => setStatutFilter(e.target.value as any)}
              className="px-2 py-1 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded"
            >
              <option value="en_attente">En attente</option>
              <option value="traite">Traités</option>
              <option value="rejete">Rejetés</option>
              <option value="tous">Tous</option>
            </select>
            <span className="text-xs text-gray-500">{signalements.length} signalement(s)</span>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {signalements.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                Aucun signalement {statutFilter !== 'tous' && `(${statutFilter})`}
              </div>
            )}
            {signalements.map((s) => (
              <div key={s.id} className="p-3 hover:bg-gray-50 dark:hover:bg-gray-900/30">
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                    s.statut === 'en_attente' ? 'bg-orange-500 animate-pulse' :
                    s.statut === 'traite' ? 'bg-green-500' : 'bg-gray-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Message de {s.message.expediteur.prenoms} {s.message.expediteur.nom}
                      </span>
                      <span className="text-[11px] text-gray-500">signalé par</span>
                      <span className="text-xs text-gray-700 dark:text-gray-300">{s.signaleur.prenoms} {s.signaleur.nom}</span>
                      <span className="text-[10px] text-gray-400 ml-auto">{new Date(s.date_signalement).toLocaleString('fr-FR')}</span>
                    </div>
                    <div className="mt-1.5 px-3 py-2 bg-gray-100 dark:bg-gray-900/50 rounded text-xs text-gray-700 dark:text-gray-200 italic">
                      <div className="text-[10px] font-semibold mb-1 text-orange-600 dark:text-orange-400">
                        Motif : {s.motif}
                      </div>
                      <div className="border-l-2 border-gray-400 dark:border-gray-600 pl-2 mt-1">
                        <div className="font-mono text-[10px] text-gray-500">Message :</div>
                        <div className={`whitespace-pre-wrap break-words ${s.message.supprime ? 'line-through opacity-60' : ''}`}>
                          {s.message.contenu || (s.message.fichier_url && '(fichier joint)') || '(vide)'}
                        </div>
                        {s.message.fichier_url && (
                          <a href={s.message.fichier_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline text-[11px]">
                            Voir le fichier
                          </a>
                        )}
                      </div>
                    </div>
                    {s.statut === 'en_attente' && (
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          onClick={() => traiterSignalement(s.id, 'supprimer_message')}
                          className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 rounded inline-flex items-center gap-1"
                          disabled={s.message.supprime}
                        >
                          <Trash2 size={11} /> Supprimer le message
                        </button>
                        <button
                          onClick={() => traiterSignalement(s.id, 'rejeter')}
                          className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 rounded inline-flex items-center gap-1"
                        >
                          <Check size={11} /> Rejeter (rien à faire)
                        </button>
                        <a
                          href="#"
                          onClick={(e) => { e.preventDefault(); loadDetail(s.conversation_id); }}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          → Voir conversation
                        </a>
                      </div>
                    )}
                    {s.statut !== 'en_attente' && (
                      <div className="mt-1 text-[10px] text-gray-500">
                        {s.statut === 'traite' ? '✓ Traité' : '✗ Rejeté'}
                        {s.traite_par_nom && ` par ${s.traite_par_nom}`}
                        {s.note_admin && ` — ${s.note_admin}`}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
      <>
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3 flex-wrap">
        <MessageSquare size={18} className="text-blue-500" />
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Modération du chat étudiants</h2>
        <div className="flex-1" />
        <select
          value={type}
          onChange={(e) => setType(e.target.value as any)}
          className="px-2 py-1 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded"
        >
          <option value="">Tous types</option>
          <option value="direct">1-à-1</option>
          <option value="groupe">Groupes</option>
        </select>
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher matricule…"
            className="pl-7 pr-3 py-1 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded"
          />
        </div>
      </div>

      <div className="p-3 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40">
        {loading ? 'Chargement…' : `${convs.length} conversation(s) — étape 1 lecture seule. La suspension/suppression viendra en étape 2.`}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/60 text-[10px] uppercase font-semibold text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Participants</th>
              <th className="px-3 py-2 text-left">Dernier message</th>
              <th className="px-3 py-2 text-center">Msgs</th>
              <th className="px-3 py-2 text-center">Signalés</th>
              <th className="px-3 py-2 text-left">Activité</th>
              <th className="px-3 py-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {convs.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    c.type === 'direct'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                  }`}>
                    {c.type === 'direct' ? '1-1' : 'GROUPE'}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {c.participants.slice(0, 3).map((p) => (
                      <span key={p.matricule} className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                        {p.prenoms} {p.nom}
                      </span>
                    ))}
                    {c.participants.length > 3 && (
                      <span className="text-[11px] text-gray-500">+{c.participants.length - 3}</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 max-w-xs truncate">
                  {c.dernier_message_extrait || <span className="italic">Aucun</span>}
                </td>
                <td className="px-3 py-2 text-center text-xs">{c.nb_messages}</td>
                <td className="px-3 py-2 text-center">
                  {c.nb_signales > 0 ? (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-[10px] font-bold">
                      <Flag size={10} /> {c.nb_signales}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-[11px] text-gray-500">
                  {new Date(c.derniere_activite).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </td>
                <td className="px-3 py-2 text-center">
                  <button
                    onClick={() => loadDetail(c.id)}
                    className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 rounded inline-flex items-center gap-1"
                  >
                    <Eye size={11} /> Voir
                  </button>
                </td>
              </tr>
            ))}
            {convs.length === 0 && !loading && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-500 text-sm">Aucune conversation</td></tr>
            )}
          </tbody>
        </table>
      </div>

      </>
      )}

      {selectedId && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Conversation #{selectedId}
                {detail && <span className="ml-2 text-xs font-normal text-gray-500">({detail.participants.length} participants, {detail.messages.length} messages)</span>}
              </h3>
              <div className="flex items-center gap-2">
                {selectedId && (
                  <button
                    onClick={() => archiverConv(selectedId)}
                    className="text-xs px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 rounded inline-flex items-center gap-1"
                    title={detail?.conversation?.archivee ? 'Désarchiver' : 'Archiver (cache aux étudiants)'}
                  >
                    <Archive size={12} /> {detail?.conversation?.archivee ? 'Désarchiver' : 'Archiver'}
                  </button>
                )}
                <button onClick={() => { setSelectedId(null); setDetail(null); }} className="text-gray-400 hover:text-gray-700">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {loadingDetail && <div className="text-center py-8 text-sm text-gray-500"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Chargement…</div>}
              {detail && (
                <>
                  <div className="mb-3 flex flex-wrap gap-1.5">
                    {detail.participants.map((p) => (
                      <div key={p.matricule} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-xs">
                        {p.photo_url ? (
                          <img src={p.photo_url} alt="" className="w-5 h-5 rounded-full" />
                        ) : (
                          <Users size={12} />
                        )}
                        <span className="font-medium">{p.prenoms} {p.nom}</span>
                        <span className="font-mono text-[10px] text-gray-500">{p.matricule}</span>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2 border-t border-gray-200 dark:border-gray-700 pt-3">
                    {detail.messages.map((m) => (
                      <div key={m.id} className="flex gap-2 text-sm">
                        {m.expediteur.photo_url ? (
                          <img src={m.expediteur.photo_url} alt="" className="w-7 h-7 rounded-full flex-shrink-0" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                            {(m.expediteur.prenoms[0] || '') + (m.expediteur.nom[0] || '')}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className="font-semibold text-xs text-gray-900 dark:text-gray-100">{m.expediteur.prenoms} {m.expediteur.nom}</span>
                            <span className="text-[10px] text-gray-500">{new Date(m.date_envoi).toLocaleString('fr-FR')}</span>
                            {m.supprime && <span className="text-[10px] px-1 py-0 rounded bg-red-100 text-red-700">supprimé</span>}
                          </div>
                          <div className={`text-sm whitespace-pre-wrap break-words ${m.supprime ? 'italic text-gray-400 line-through' : 'text-gray-700 dark:text-gray-200'}`}>
                            {m.contenu}
                          </div>
                          {m.fichier_url && (
                            <a href={m.fichier_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline">
                              📎 fichier
                            </a>
                          )}
                        </div>
                        {!m.supprime && (
                          <button
                            onClick={() => supprimerMessageDetail(m.id)}
                            className="text-xs text-gray-400 hover:text-red-500 px-2"
                            title="Supprimer ce message (modération)"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                    {detail.messages.length === 0 && (
                      <div className="text-center py-6 text-xs text-gray-500">Aucun message dans cette conversation</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
